import { useState, useMemo, useEffect } from 'react';
import { TimelineItem, ItemFilters, DisplayMode, ActiveFunctions, FunctionType } from '@/types/item';
import ItemsHeader from './ItemsHeader';
import DraggableItemsTimeline from './DraggableItemsTimeline';
import ItemEditDialog from './ItemEditDialog';
import ItemsFunctionDialog from './ItemsFunctionDialog';
import TimelineReportDialog from '@/components/timeline-report/TimelineReportDialog';
import ItemsPerformanceMonitor from './ItemsPerformanceMonitor';
import { useDeleteItem } from '@/hooks/useItems';
import { useItemsProjectsStrict } from '@/hooks/useItemsProjectsStrict';
import { useItemsWithMilestoneFilter } from '@/hooks/useItemsWithMilestoneFilter';
import { useItemsCategoryPermissions } from '@/hooks/useItemsPermissions';
import { usePagination } from '@/hooks/usePagination';
import { 
  Pagination, 
  PaginationContent, 
  PaginationItem, 
  PaginationLink, 
  PaginationNext, 
  PaginationPrevious,
  PaginationEllipsis
} from '@/components/ui/pagination';
import { filterItemsByPermissionsAndFilters } from '@/lib/itemsFilter';
import { groupAndSortItemsByCategory } from '@/utils/itemsCategoryGrouping';
import { useLocalStorageManager } from '@/hooks/useLocalStorageManager';
import { useItemCategories } from '@/hooks/useItemCategories';
import { useItemsFiltersState } from '@/hooks/useItemsFiltersState';
import { useItemsPerformanceMonitor } from '@/hooks/useItemsPerformanceMonitor';
import { globalFeatureFlags } from '@/lib/featureFlags';
import { useItemsMilestones } from '@/hooks/milestones/useItemMilestones';
import { formatDateForBackend } from '@/utils/dateFormatUtils';
import { useDialogStatePersisted } from '@/hooks/useDialogStatePersisted';

const PERF_MONITOR_ENABLED = import.meta.env.VITE_ITEMS_PERF_MONITOR === 'true';
import { sanitizeSearchQuery, searchRateLimiter } from '@/utils/inputSanitization';

interface ItemsManagementProps {
  initialProject?: string;
  initialAnchor?: string; // YYYY-MM-DD format
  hideStatusFilter?: boolean;
  restrictedProjectIds?: string[]; // Optional: restrict items to specific projects (e.g., service projects)
  itemTypeFilter?: 'build' | 'service'; // ✅ Item type filter - undefined = show all
  assigneeFilter?: string[]; // ✅ Filter by assigned user IDs (service projects only, feature flag: ENABLE_SERVICE_ITEM_ASSIGNMENT)
  showItemTypeBadges?: boolean; // Show SVC/BLD badges (default: false, only true in Service context)
  hideFunctionsSection?: boolean; // Hide Functions filter section
  hideGatesSection?: boolean; // Hide Gates filter section
  hideSearchInput?: boolean; // Hide search input field
}

const ItemsManagement = ({ 
  initialProject, 
  initialAnchor, 
  hideStatusFilter = false, 
  restrictedProjectIds,
  itemTypeFilter, // ✅ undefined = show all items
  assigneeFilter, // ✅ undefined = show all assignees
  showItemTypeBadges = false, // Default: hide badges (only show in Service context)
  hideFunctionsSection = false,
  hideGatesSection = false,
  hideSearchInput = false
}: ItemsManagementProps = {}) => {
  const isItemTypeEnabled = globalFeatureFlags.isEnabled('ENABLE_ITEM_TYPE_FILTERING');
  const { getStandardValue, setStandardValue } = useLocalStorageManager();
  const [selectedProject, setSelectedProject] = useState<string>(initialProject || '');
  // Persist selectedItemId to localStorage to maintain dialog state across navigation
  const [selectedItemId, setSelectedItemId] = useState<string | null>(() => {
    return getStandardValue('items-edit-selected-id', null, 'APP');
  });
  const editDialog = useDialogStatePersisted('items-edit-dialog');
  
  // Persist selectedItemId changes to localStorage
  useEffect(() => {
    setStandardValue('items-edit-selected-id', selectedItemId, 'APP');
  }, [selectedItemId, setStandardValue]);
  
  const [selectedFunction, setSelectedFunction] = useState<{ itemId: string; functionType: FunctionType } | null>(null);
  const [anchorDate, setAnchorDate] = useState<string>(() => {
    // Try to load from standardized localStorage first
    const savedAnchor = getStandardValue('items-anchor-date', null, 'SETTINGS');
    if (savedAnchor && typeof savedAnchor === 'string') {
      return savedAnchor;
    }
    
    // Migration: check for old anchorMonth format
    const savedAnchorMonth = getStandardValue('items-anchor-month', null, 'SETTINGS');
    if (savedAnchorMonth && savedAnchorMonth.year && savedAnchorMonth.month) {
      const migrationDate = new Date(savedAnchorMonth.year, savedAnchorMonth.month - 1, 1);
      return formatDateForBackend(migrationDate)!;
    }
    
    if (initialAnchor) {
      console.debug('[ItemsManagement] Setting initial anchor from param:', initialAnchor);
      return initialAnchor;
    }
    
    const now = new Date();
    return formatDateForBackend(now)!;
  });
  
  const [showProjectColumn, setShowProjectColumn] = useState(true);
  const [showCategoryColumn, setShowCategoryColumn] = useState(false);
  const [groupByCategory, setGroupByCategory] = useState(false);
  const [showPerformanceMonitor, setShowPerformanceMonitor] = useState(false);
  const [showTimelineReport, setShowTimelineReport] = useState(false);
  const [isReorderMode, setIsReorderMode] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  // Get filter state management
  const {
    filters,
    activeFunctions,
    displayMode,
    updateFilters,
    updateActiveFunctions,
    updateDisplayMode,
    clearFilters,
    getActiveFiltersCount,
  } = useItemsFiltersState();

  // Fetch data from backend
  const { data: projects = [], isLoading: projectsLoading } = useItemsProjectsStrict();
  
  // ✅ Fix: Compute effectiveProjectIds as intersection of allowed + restricted
  const effectiveProjectIds = useMemo(() => {
    if (projectsLoading) return [];
    const baseProjectIds = projects.map(p => p.id);
    
    // If restrictedProjectIds specified, intersect with user's allowed projects
    if (restrictedProjectIds && restrictedProjectIds.length > 0) {
      const restrictedSet = new Set(restrictedProjectIds);
      const intersection = baseProjectIds.filter(id => restrictedSet.has(id));
      console.debug('[ItemsManagement] effectiveProjectIds', {
        base: baseProjectIds.length,
        restricted: restrictedProjectIds.length,
        effective: intersection.length
      });
      return intersection;
    }
    
    return baseProjectIds;
  }, [projects, projectsLoading, restrictedProjectIds]);
  
  // Defensive: Ensure selectedProject is valid against restrictedProjectIds
  const safeSelectedProject = useMemo(() => {
    if (!selectedProject) return '';
    if (restrictedProjectIds && !restrictedProjectIds.includes(selectedProject)) {
      console.warn('[ItemsManagement] Restricted project selection corrected:', selectedProject);
      return '';
    }
    return selectedProject;
  }, [selectedProject, restrictedProjectIds]);

  // Auto-correction: Reset selectedProject if it becomes invalid
  useEffect(() => {
    if (restrictedProjectIds && selectedProject && !restrictedProjectIds.includes(selectedProject)) {
      if (import.meta.env.DEV) {
        console.info('[ItemsManagement] Auto-correcting invalid project selection:', selectedProject);
      }
      setSelectedProject('');
    }
  }, [restrictedProjectIds, selectedProject]);
  
  const { data: items = [], isLoading, error } = useItemsWithMilestoneFilter({
    projectId: safeSelectedProject || undefined,
    allowedProjectIds: safeSelectedProject ? undefined : (projectsLoading ? undefined : effectiveProjectIds),
    searchTerm: filters.search,
    category: filters.category,
    filters,
    itemType: isItemTypeEnabled ? itemTypeFilter : undefined // ✅ Pass undefined when feature disabled
  });
  const deleteItem = useDeleteItem();
  const { canViewCategory } = useItemsCategoryPermissions();
  const { data: categoriesData } = useItemCategories();

  // Stable, sorted item ids for depends_on queries (settle once per cold-load;
  // unaffected by sort/paginate/permission/edit-bar). Threaded into
  // ItemsTimeline so useItemsDependsOn / useMilestonesDependsOn don't refire
  // on every list churn.
  const stableDepItemIds = useMemo(
    () => (items || []).map(i => i.id).slice().sort(),
    [items]
  );


  
  
  // ✅ Monitoring: Log filter usage
  useEffect(() => {
    if (isItemTypeEnabled && itemTypeFilter) {
      console.log('[ITEM_TYPE_FILTER]', {
        filter: itemTypeFilter,
        itemCount: items.length,
        timestamp: new Date().toISOString()
      });
    }
  }, [isItemTypeEnabled, itemTypeFilter, items.length]);
  
  // Performance monitoring (only if enabled)
  const performanceMonitor = PERF_MONITOR_ENABLED 
    ? useItemsPerformanceMonitor(items?.length || 0)
    : null;
  
  const {
    recordFilterPerformance = () => {},
    recordRenderPerformance = () => {}
  } = performanceMonitor || {};

  // Save anchor date to localStorage when it changes
  useEffect(() => {
    setStandardValue('items-anchor-date', anchorDate, 'SETTINGS');
  }, [anchorDate, setStandardValue]);

  // Auto-set anchor month to earliest item start date when items load (only if no saved anchor)
  useEffect(() => {
    const savedAnchor = getStandardValue('items-anchor-date', null, 'SETTINGS');
    if (items.length > 0 && !savedAnchor && !initialAnchor) {
      // Extract dates from timeline data since TimelineItem uses different structure
      const allDates: Date[] = [];
      items.forEach(item => {
        if (item.timelineData && typeof item.timelineData === 'object') {
          Object.values(item.timelineData).forEach(functionData => {
            if (functionData && typeof functionData === 'object' && 'g1' in functionData) {
              const g1Data = functionData.g1;
              if (g1Data && typeof g1Data === 'object' && 'start' in g1Data && g1Data.start) {
                allDates.push(new Date(g1Data.start as string));
              }
            }
          });
        }
      });
      
      if (allDates.length > 0) {
        const earliestDate = new Date(Math.min(...allDates.map(d => d.getTime())));
        const earliestDateStr = formatDateForBackend(earliestDate)!;
        if (earliestDateStr !== anchorDate) {
          console.debug('[ItemsManagement] Auto-setting anchor to earliest date:', earliestDateStr);
          setAnchorDate(earliestDateStr);
        }
      }
    }
  }, [items, getStandardValue, anchorDate, initialAnchor]);

  // Auto-disable reorder mode when sorting by date
  useEffect(() => {
    if (isReorderMode && filters.sortBy && filters.sortBy !== 'custom') {
      setIsReorderMode(false);
      console.debug('[ItemsManagement] Reorder mode disabled due to date sorting');
    }
  }, [filters.sortBy, isReorderMode]);

  const filteredItems = useMemo(() => {
    // Return empty array if projects are still loading
    if (projectsLoading) {
      return [];
    }

    // Apply rate limiting for search operations
    if (filters.search && !searchRateLimiter.isAllowed('items-search')) {
      console.warn('[ItemsManagement] Search rate limit exceeded');
      return [];
    }

    let baseFilteredItems = filterItemsByPermissionsAndFilters(items, {
      filters: {
        ...filters,
        search: sanitizeSearchQuery(filters.search),
      },
      activeFunctions,
      selectedProject: safeSelectedProject,
      allowedProjectIds: effectiveProjectIds,
      canViewCategory,
      enableDebugLog: import.meta.env.VITE_ITEMS_FILTER_DEBUG === 'true',
      logPrefix: 'ItemsManagement'
    });
    
    // Apply assignee filter (for service projects only, feature flag: ENABLE_SERVICE_ITEM_ASSIGNMENT)
    if (assigneeFilter && assigneeFilter.length > 0) {
      baseFilteredItems = baseFilteredItems.filter(item => {
        // Include items with matching assignee OR unassigned items if 'unassigned' is in filter
        if (assigneeFilter.includes('unassigned') && !item.assigned_user_id) {
          return true;
        }
        return item.assigned_user_id && assigneeFilter.includes(item.assigned_user_id);
      });
    }

    // Sort by user preference with direction
    const sortBy = filters.sortBy || 'custom';
    const direction = filters.sortDirection || 'asc';
    const multiplier = direction === 'asc' ? 1 : -1;
    
    const sortedItems = [...baseFilteredItems].sort((a, b) => {
      if (sortBy === 'start') {
        const dateA = new Date(a.startDate).getTime();
        const dateB = new Date(b.startDate).getTime();
        return (dateA - dateB) * multiplier;
      } else if (sortBy === 'end') {
        const dateA = new Date(a.endDate).getTime();
        const dateB = new Date(b.endDate).getTime();
        return (dateA - dateB) * multiplier;
      } else {
        // Custom: Sort by sort_order
        const orderA = a.sortOrder ?? Number.MAX_SAFE_INTEGER;
        const orderB = b.sortOrder ?? Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      }
    });

    // Apply category grouping if enabled
    let finalItems = sortedItems;
    if (groupByCategory && categoriesData) {
      const skipInternalSort = sortBy === 'start' || sortBy === 'end';
      finalItems = groupAndSortItemsByCategory(sortedItems, categoriesData, skipInternalSort);
    }
    
    return finalItems;
  }, [items, filters, activeFunctions, safeSelectedProject, effectiveProjectIds, projectsLoading, canViewCategory, groupByCategory, categoriesData, assigneeFilter]);

  // Pagination for Build items (50 per page)
  const buildPagination = usePagination(filteredItems.length, {
    pageSize: 50,
    initialPage: 1
  });

  // Paginate items
  const paginatedItems = useMemo(() => {
    const start = (buildPagination.currentPage - 1) * buildPagination.pageSize;
    const end = start + buildPagination.pageSize;
    return filteredItems.slice(start, end);
  }, [filteredItems, buildPagination.currentPage, buildPagination.pageSize]);

  // Reset pagination when filters change
  useEffect(() => {
    buildPagination.setTotalItems(filteredItems.length);
    buildPagination.setPage(1);
  }, [filteredItems.length]);

  // Track performance separately from filtering to avoid circular dependencies
  useEffect(() => {
    if (!PERF_MONITOR_ENABLED) return;
    
    const filterStart = performance.now();
    
    if (filteredItems.length > 0) {
      recordFilterPerformance(filteredItems.length);
      
      const filterTime = performance.now() - filterStart;
      recordRenderPerformance({
        filterProcessingTime: filterTime,
      });
    }
  }, [filteredItems, recordFilterPerformance, recordRenderPerformance]);

  const handleItemClick = (itemId: string) => {
    setSelectedItemId(itemId);
    editDialog.open();
  };

  const handleItemSave = () => {
    // Backend update will be handled by the hooks, just close dialog
    setSelectedItemId(null);
    editDialog.close();
  };

  const handleFunctionClick = (item: TimelineItem, functionType: FunctionType) => {
    setSelectedFunction({ itemId: item.id, functionType });
  };


  const handleDeleteItem = (itemId: string) => {
    deleteItem.mutate(itemId);
  };

  const handleTimelineReport = () => {
    setShowTimelineReport(true);
  };
  
  // ✅ HARDCODED (2025-11-09): Milestone expand/collapse always enabled
  const milestoneFeatureEnabled = true;
  const itemIds = filteredItems.map(item => item.id);
  const { data: milestones = [] } = useItemsMilestones(
    milestoneFeatureEnabled ? itemIds : [],
    { enabled: milestoneFeatureEnabled && itemIds.length > 0 }
  );
  
  const milestonesByItem = useMemo(() => {
    if (!milestoneFeatureEnabled || milestones.length === 0) return {};
    return milestones.reduce((acc, m) => {
      if (!acc[m.item_id]) acc[m.item_id] = [];
      acc[m.item_id].push(m);
      return acc;
    }, {} as Record<string, typeof milestones>);
  }, [milestones, milestoneFeatureEnabled]);
  
  const filterMilestones = (
    itemMilestones: any[],
    activeMilestoneTypes?: string[],
    milestoneName?: string
  ): any[] => {
    const hasTypeFilter = activeMilestoneTypes && activeMilestoneTypes.length > 0;
    const hasNameFilter = milestoneName && milestoneName.trim();
    
    if (!hasTypeFilter && !hasNameFilter) return itemMilestones;
    
    return itemMilestones.filter(m => {
      const typeMatch = !hasTypeFilter || activeMilestoneTypes.includes(m.milestone_type || 'default');
      const nameMatch = !hasNameFilter || (
        m.name && 
        m.name.toLowerCase().includes(milestoneName!.trim().toLowerCase())
      );
      return typeMatch && nameMatch;
    });
  };
  
  const totalItemsWithMilestones = useMemo(() => {
    if (!milestoneFeatureEnabled || isReorderMode) return 0;
    return filteredItems.filter(item => {
      const itemMilestones = milestonesByItem[item.id] || [];
      const filtered = filterMilestones(itemMilestones, filters.activeMilestoneTypes, filters.milestoneName);
      return filtered.length > 0;
    }).length;
  }, [filteredItems, milestonesByItem, filters.activeMilestoneTypes, filters.milestoneName, milestoneFeatureEnabled, isReorderMode]);
  
  const handleExpandAll = () => {
    if (isReorderMode) return;
    
    const itemsWithMilestones = filteredItems
      .filter(item => {
        const itemMilestones = milestonesByItem[item.id] || [];
        const filtered = filterMilestones(itemMilestones, filters.activeMilestoneTypes, filters.milestoneName);
        return filtered.length > 0;
      })
      .map(item => item.id);
    
    setExpandedItems(new Set(itemsWithMilestones));
  };
  
  const handleCollapseAll = () => {
    setExpandedItems(new Set());
  };
  
  const handleToggleExpand = (itemId: string) => {
    if (isReorderMode) return;
    
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  };

  const selectedItem = selectedItemId ? items.find(item => item.id === selectedItemId) : null;
  const selectedFunctionItem = selectedFunction ? items.find(item => item.id === selectedFunction.itemId) : null;

  const alignedAnchorDate = useMemo(() => {
    const allDates: string[] = [];

    (items as any[]).forEach((it: any) => {
      if (it.startDate) allDates.push(String(it.startDate).slice(0, 10));
    });

    if (allDates.length === 0) return null;

    const earliest = allDates.reduce((a, b) => (a < b ? a : b));

    const d = new Date(earliest + 'T00:00:00');

    d.setDate(d.getDate() - 7);

    return formatDateForBackend(d)!;
  }, [items]);

  const handleAlignAnchorToStart = () => {
    if (alignedAnchorDate) setAnchorDate(alignedAnchorDate);
  };

  if (error) {

    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-center">
          <h3 className="text-lg font-semibold text-destructive mb-2">Error Loading Items</h3>
          <p className="text-muted-foreground">{error.message}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="items-management space-y-6">
      <ItemsHeader
        selectedProject={safeSelectedProject}
        onProjectChange={setSelectedProject}
        activeFunctions={activeFunctions}
        onActiveFunctionsChange={updateActiveFunctions}
        displayMode={displayMode}
        onDisplayModeChange={updateDisplayMode}
        filters={filters}
        onFiltersChange={updateFilters}
        anchorDate={anchorDate}
        onAnchorDateChange={setAnchorDate}
        onAlignAnchorToStart={handleAlignAnchorToStart}
        alignAnchorDisabled={items.length === 0}
        alignAnchorActive={!!alignedAnchorDate && anchorDate === alignedAnchorDate}

        showProjectColumn={showProjectColumn}
        onShowProjectColumnChange={setShowProjectColumn}
        showCategoryColumn={showCategoryColumn}
        onShowCategoryColumnChange={setShowCategoryColumn}
        groupByCategory={groupByCategory}
        onGroupByCategoryChange={setGroupByCategory}
        onTimelineReport={handleTimelineReport}
        isLoading={isLoading}
        hideStatusFilter={hideStatusFilter}
        canReorder={true}
        reorderModeEnabled={isReorderMode}
        onReorderModeChange={setIsReorderMode}
        onTogglePerformanceMonitor={() => setShowPerformanceMonitor(!showPerformanceMonitor)}
        showPerformanceMonitor={showPerformanceMonitor}
        restrictedProjectIds={restrictedProjectIds}
        onExpandAllMilestones={handleExpandAll}
        onCollapseAllMilestones={handleCollapseAll}
        expandedItemsCount={expandedItems.size}
        totalItemsWithMilestones={totalItemsWithMilestones}
        hideFunctionsSection={true}
        hideGatesSection={true}
        compactProjectSelector={true}
        hideAnchorLabel={true}
        hideSearchInput={hideSearchInput}
      />

      {((isLoading && items === undefined) || projectsLoading) ? (
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
            <p className="text-muted-foreground">Loading items...</p>
          </div>
        </div>
      ) : (
        <>

          <DraggableItemsTimeline
            items={paginatedItems}
            activeFunctions={activeFunctions}
            displayMode={displayMode}
            anchorDate={anchorDate}
            showProjectColumn={showProjectColumn}
            showCategoryColumn={showCategoryColumn}
            filters={filters}
            onItemClick={handleItemClick}
            onFunctionClick={handleFunctionClick}
            onDeleteItem={handleDeleteItem}
            projectId={safeSelectedProject}
            expandedItems={expandedItems}
            onToggleExpand={handleToggleExpand}
            onExpandAll={handleExpandAll}
            onCollapseAll={handleCollapseAll}
            showItemTypeBadges={showItemTypeBadges}
            stableItemIds={stableDepItemIds}
          />

          {/* Pagination Controls */}
          {buildPagination.totalPages > 1 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 px-3 sm:px-4 py-3 sm:py-4 mt-6 border-t bg-muted/30 rounded-lg">
              <div className="text-xs sm:text-sm text-muted-foreground text-center sm:text-left">
                Hiển thị {buildPagination.startItem}-{buildPagination.endItem} trong tổng số {buildPagination.totalItems} items
              </div>
              <div className="overflow-x-auto max-w-full">
                <Pagination>
                  <PaginationContent className="flex-wrap justify-center">
                  <PaginationItem>
                    <PaginationPrevious 
                      onClick={() => buildPagination.setPage(buildPagination.currentPage - 1)}
                      className={!buildPagination.hasPreviousPage ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                    />
                  </PaginationItem>
                  
                  {(() => {
                    const totalPages = buildPagination.totalPages;
                    const current = buildPagination.currentPage;
                    const pages: (number | 'ellipsis')[] = [];

                    if (totalPages <= 7) {
                      for (let i = 1; i <= totalPages; i++) pages.push(i);
                    } else {
                      pages.push(1);
                      if (current <= 4) {
                        for (let i = 2; i <= 5; i++) pages.push(i);
                        pages.push('ellipsis');
                        pages.push(totalPages);
                      } else if (current >= totalPages - 3) {
                        pages.push('ellipsis');
                        for (let i = totalPages - 4; i <= totalPages; i++) pages.push(i);
                      } else {
                        pages.push('ellipsis');
                        for (let i = current - 1; i <= current + 1; i++) pages.push(i);
                        pages.push('ellipsis');
                        pages.push(totalPages);
                      }
                    }
                    return pages;
                  })().map((page, idx) => (
                    <PaginationItem key={`page-${idx}`}>
                      {page === 'ellipsis' ? (
                        <PaginationEllipsis />
                      ) : (
                        <PaginationLink
                          onClick={() => buildPagination.setPage(page)}
                          isActive={buildPagination.currentPage === page}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      )}
                    </PaginationItem>
                  ))}
                  
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => buildPagination.setPage(buildPagination.currentPage + 1)}
                        className={!buildPagination.hasNextPage ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            </div>
          )}
        </>
      )}

      <div className="bg-muted/50 p-4 rounded-lg">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-muted-foreground">Tổng Items:</span>
            <span className="ml-2 font-medium">{filteredItems.length}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Có G4:</span>
            <span className="ml-2 font-medium">
              {filteredItems.filter(item => 
                Object.values(item.timelineData).some(functionData => {
                  if (functionData && typeof functionData === 'object' && 'g4' in functionData) {
                    const g4Data = functionData.g4;
                    return g4Data && typeof g4Data === 'object' && ('start' in g4Data || 'end' in g4Data);
                  }
                  return false;
                })
              ).length}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">G5 = 100%:</span>
            <span className="ml-2 font-medium">
              {filteredItems.filter(item => 
                Object.values(item.timelineData).every(functionData => {
                  if (functionData && typeof functionData === 'object' && 'g5' in functionData) {
                    const g5Data = functionData.g5;
                    return g5Data && typeof g5Data === 'object' && 'isComplete' in g5Data && g5Data.isComplete;
                  }
                  return false;
                })
              ).length}
            </span>
          </div>
          <div>
            <span className="text-muted-foreground">Quá tải:</span>
            <span className="ml-2 font-medium text-destructive">
              {filteredItems.filter(item => item.resourceStatus === 'over').length}
            </span>
          </div>
        </div>
      </div>

      <ItemEditDialog
        item={selectedItem}
        availableItems={filteredItems}
        isOpen={editDialog.isOpen && !!selectedItemId}
        onClose={() => {
          setSelectedItemId(null);
          editDialog.close();
        }}
        onSave={handleItemSave}
      />

      <ItemsFunctionDialog
        item={selectedFunctionItem}
        functionType={selectedFunction?.functionType || null}
        isOpen={!!selectedFunction}
        onClose={() => setSelectedFunction(null)}
      />

      <TimelineReportDialog
        isOpen={showTimelineReport}
        onClose={() => setShowTimelineReport(false)}
        items={items}
        projectId={safeSelectedProject || undefined}
        filters={filters}
        activeFunctions={activeFunctions}
      />

        {PERF_MONITOR_ENABLED && (
          <ItemsPerformanceMonitor isVisible={showPerformanceMonitor} />
        )}
    </div>
  );
};

export default ItemsManagement;